import { useEffect, useMemo, useState } from 'react'
import { Composer } from './Composer.jsx'
import { MessageList } from './MessageList.jsx'
import { PanelHeader } from './PanelHeader.jsx'
import { ChatMenuDrawer } from './ChatMenuDrawer.jsx'
import { SuggestionsStrip } from './SuggestionsStrip.jsx'
import { LeadFormInPanel } from './FormOverlay.jsx'
import { OrderStatusInPanel } from './OrderStatusPanel.jsx'
import { useChatLayout } from '../../context/ChatLayoutContext.jsx'
import { useForm } from '../../context/FormContext.jsx'
import { useOrderStatus } from '../../context/OrderStatusContext.jsx'
import {
  combineSuggestions,
  shouldShowSuggestionsStrip,
  shouldUseInlineSuggestions,
} from '../../lib/chatUtils.js'
import { useIntelligentWeb } from '../../context/IntelligentWebContext.jsx'
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport.js'
import { useFrozenWhileStreaming } from '../../hooks/useFrozenWhileStreaming.js'
import { resolveSuggestionsLayoutKey } from '../../templates/index.js'
import { cn } from '../../lib/cn.js'

/**
 * Generic chat panel chrome. Used by every layout (floating bubble,
 * overlay, slide-out, injected) - layouts handle positioning, this
 * component handles the internal composition: header, scrolling
 * message list, composer.
 */
export function ChatPanel({
  conf,
  messages,
  isTyping,
  onSend,
  onSendMenuAction,
  onClose,
  ochatbot,
  className,
  showClose = true,
  /** When false, parent renders suggestions (e.g. overlay split column). */
  embedSuggestions = true,
  headerClassName,
  onLeadFormSubmit,
  onOrderStatusResult,
  onRestartConversation,
}) {
  const { suggestions } = useChatLayout()
  const { activeCtas, activeLeadForms, setActiveLeadForms } = useIntelligentWeb()
  const { isFormOpen, formColumn, openForm } = useForm()
  const { isOrderStatusOpen, orderStatusColumn, openOrderStatus } =
    useOrderStatus()
  const [menuOpen, setMenuOpen] = useState(false)
  const menuItems = useMemo(
    () => (Array.isArray(conf?.menuItems) ? conf.menuItems : []),
    [conf?.menuItems],
  )
  const layoutKey = useMemo(() => resolveSuggestionsLayoutKey(conf), [conf])
  const isMobileViewport = useIsMobileViewport()
  const useInline = shouldUseInlineSuggestions(conf, layoutKey) || isMobileViewport
  const showFormHere = isFormOpen && (embedSuggestions || formColumn === 'left')
  const showOrderStatusHere =
    isOrderStatusOpen && (embedSuggestions || orderStatusColumn === 'left')
  const isStreaming = useMemo(
    () => messages.some((m) => m?.streaming),
    [messages],
  )
  const stripSuggestions = useFrozenWhileStreaming(suggestions, isStreaming)
  const combinedStripCount = useMemo(() => {
    if (isFormOpen || isOrderStatusOpen) return 0
    if (!embedSuggestions || useInline) return 0
    if (!shouldShowSuggestionsStrip(conf)) return 0
    return combineSuggestions(stripSuggestions, activeCtas, activeLeadForms, conf)
      .length
  }, [
    embedSuggestions,
    useInline,
    stripSuggestions,
    activeCtas,
    activeLeadForms,
    conf,
    isFormOpen,
    isOrderStatusOpen,
  ])

  useEffect(() => {
    // Streaming inserts placeholder bot messages; ensure focus stays
    // on the composer through the round-trip so power-users can keep
    // typing follow-ups.
  }, [messages.length])

  const panelStyle =
    conf?.widgetPanelBackgroundColor && String(conf.widgetPanelBackgroundColor).trim()
      ? { backgroundColor: conf.widgetPanelBackgroundColor.trim() }
      : undefined

  return (
    <section
      className={cn(
        'ometrics-panel flex h-full min-h-0 w-full flex-col overflow-hidden',
        'bg-[var(--color-surface)] text-[var(--color-surface-foreground)]',
        'border border-[var(--color-border)]',
        className,
      )}
      style={panelStyle}
    >
      <PanelHeader
        conf={conf}
        onClose={onClose}
        showClose={showClose}
        className={headerClassName}
        onMenuOpen={() => setMenuOpen((v) => !v)}
        menuOpen={menuOpen}
      />
      <div
        className={cn(
          'relative flex min-h-0 flex-1 flex-col',
          (showFormHere || showOrderStatusHere) && 'overflow-hidden',
        )}
      >
        <ChatMenuDrawer
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          conf={conf}
          menuItems={menuItems}
          onRestartConversation={onRestartConversation}
          onOpenOrderStatus={openOrderStatus}
          onSendMenuAction={onSendMenuAction}
          ochatbot={ochatbot}
          openForm={openForm}
          setActiveLeadForms={setActiveLeadForms}
        />
        <MessageList
          messages={messages}
          isTyping={isTyping}
          conf={conf}
          ochatbot={ochatbot}
          layoutKey={layoutKey}
          onSend={onSend}
          panelSuggestionsCount={combinedStripCount}
        />
        {combinedStripCount > 0 ? (
          <SuggestionsStrip
            suggestions={stripSuggestions}
            variant="panel"
            layoutKey={layoutKey}
            onSend={onSend}
            ochatbot={ochatbot}
          />
        ) : null}
        {showFormHere ? (
          <LeadFormInPanel onSubmit={onLeadFormSubmit} conf={conf} />
        ) : null}
        {showOrderStatusHere ? (
          <OrderStatusInPanel conf={conf} onResult={onOrderStatusResult} />
        ) : null}
      </div>
      {!showFormHere && !showOrderStatusHere ? (
        <Composer
          onSend={onSend}
          disabled={isTyping}
          conf={conf}
          placeholder={conf?.composerPlaceholder || 'Type a message…'}
        />
      ) : null}
    </section>
  )
}
